import React, { useMemo, useRef } from "react";

import Condition from "../../components/Conditional/Condition";
import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { APP_MODE } from "../../contexts/ConfigContext/types";
import ChangeDineInTakeawayModal from "../CartPage/CartModals/ChangeDineInTakeawayModal";
import NoCatalogue from "./NoCatalogue";
import { GoToCartButton } from "./ProductList/GoToCartButton/GoToCartButton";
import ProductList from "./ProductList/ProductList";
import { ViewOnlyModeAlert } from "./ProductList/ViewOnlyModeAlert";
import { useViewOnlyModeAlert } from "./ProductList/ViewOnlyModeAlert/useViewOnlyModeAlert";
import "./ProductListPage.scss";
import { useHandleClickProduct } from "./ProductSection/hooks";
import { useCatalogueData } from "./hooks/useCatalogueData";
import { useListPageHandler } from "./hooks/useListPageHandler";
import { usePLPDineInTakeaway } from "./usePLPDineInTakeaway";

const ProductListPage = (props: { testRef?: React.RefObject<HTMLDivElement> }) => {
	const { categories, products, isLoading } = useCatalogueData();
	const { state: cart } = useCartContext((state) => state.cart);
	const categoryListHeightRef = useRef(0);
	const {
		productListRef,
		productSectionRefs,
		bannerAnimateClass,
		handleScrollUpdateCategory,
		handleCategoryClick,
		activeCategoryId,
		isClickScrolling,
	} = useListPageHandler(categories, categoryListHeightRef);
	const handleItemClick = useHandleClickProduct();

	const { state: appMode } = useConfigContext((state) => state.appMode);
	const { state: bannerImages } = useConfigContext((state) => state.config.banner);
	const { state: allow_takeaway } = useConfigContext((state) => state.config.allow_takeaway);

	const viewModeAlert = useViewOnlyModeAlert({
		autoShow: appMode === APP_MODE.VIEW,
		persistOnClose: false,
	});

	const shouldShowViewModeAlert = useMemo(
		() => appMode === APP_MODE.VIEW && viewModeAlert.isVisible,
		[appMode, viewModeAlert.isVisible]
	);

	const { showDineInTakeaway, hideDineInTakeaway } = usePLPDineInTakeaway();

	const hasCatalogue = useMemo(() => {
		if (isLoading) return true;

		return products.length > 0;
	}, [isLoading, products]);

	return (
		<div className="ProductListPage" ref={props.testRef}>
			<Condition check={hasCatalogue}>
				<ProductList
					categories={categories}
					products={products}
					onScrollUpdateCategory={handleScrollUpdateCategory}
					listRef={productListRef}
					sectionRefs={productSectionRefs}
					isClickScrolling={isClickScrolling}
					bannerImages={bannerImages}
					bannerClassName={bannerAnimateClass}
					activeCategoryId={activeCategoryId}
					onCategoryClick={handleCategoryClick}
					onItemClick={handleItemClick}
					categoryListHeightRef={categoryListHeightRef}
				/>
				<div className="ProductListPageFooters">
					<Condition check={shouldShowViewModeAlert}>
						<ViewOnlyModeAlert />
					</Condition>
					<Condition check={!!cart?.length}>
						<GoToCartButton />
					</Condition>
				</div>
				<Condition check={!!allow_takeaway}>
					<ChangeDineInTakeawayModal isOpen={showDineInTakeaway} closeFn={hideDineInTakeaway} />
				</Condition>
			</Condition>
			<Condition check={!hasCatalogue}>
				<NoCatalogue />
			</Condition>
		</div>
	);
};

export default React.memo(ProductListPage);
